import { daysUntil } from "@/lib/evaluation-term-utils"
import {
  EVALUATION_REFERENCE_DATE,
  getTermsByPosition,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import {
  previewCourses,
  scheduleCoursesForTerm,
} from "@/lib/mock/evaluation-schedule-courses"

export interface EvaluationWorkQueueRow {
  id: string
  termLabel: string
  programName: string
  subject: string
  state: "notSetup" | "draft" | "scheduled" | "live" | "closed" | "review" | "attention"
  stateLabel: string
  responseRate?: number
  dueLabel?: string
  actionLabel: string
  priority: number
}

const STATE_LABELS: Record<EvaluationWorkQueueRow["state"], string> = {
  notSetup: "Not set up",
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  closed: "Closed",
  review: "Review feedback",
  attention: "Below threshold",
}

export function buildTermWorkQueueRows(
  term: EvaluationTerm,
  refDate: Date = EVALUATION_REFERENCE_DATE,
): EvaluationWorkQueueRow[] {
  return rowsFromTerm(term, refDate)
}

function rowsFromTerm(term: EvaluationTerm, refDate: Date): EvaluationWorkQueueRow[] {
  const rows: EvaluationWorkQueueRow[] = []
  const termLabel = `${term.academicYear} · ${term.termName}`

  if (term.counts.notSetup > 0) {
    rows.push({
      id: `${term.id}-setup`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.notSetup} courses`,
      state: "notSetup",
      stateLabel: STATE_LABELS.notSetup,
      actionLabel: "Schedule evaluations",
      priority: 1,
    })
  }

  if (term.counts.draft > 0) {
    rows.push({
      id: `${term.id}-draft`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.draft} evaluations`,
      state: "draft",
      stateLabel: STATE_LABELS.draft,
      actionLabel: "Finish setup",
      priority: 2,
    })
  }

  if (term.counts.scheduled > 0) {
    rows.push({
      id: `${term.id}-scheduled`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.scheduled} courses`,
      state: "scheduled",
      stateLabel: STATE_LABELS.scheduled,
      dueLabel: term.nextScheduledDate
        ? `Sends in ${daysUntil(term.nextScheduledDate, refDate)} days`
        : undefined,
      actionLabel: "Update schedule",
      priority: 3,
    })
  }

  if (term.counts.live > 0) {
    rows.push({
      id: `${term.id}-live`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.live} evaluations`,
      state: "live",
      stateLabel: STATE_LABELS.live,
      responseRate: term.liveResponseRate,
      dueLabel: term.nextClosingDate
        ? `Closes in ${daysUntil(term.nextClosingDate, refDate)} days`
        : undefined,
      actionLabel: "Remind or extend",
      priority: 4,
    })
  }

  if (term.counts.closed > 0 && term.timelinePosition === "current") {
    rows.push({
      id: `${term.id}-closed`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.closed} evaluations`,
      state: "closed",
      stateLabel: STATE_LABELS.closed,
      responseRate: term.closedResponseRate,
      actionLabel: "Review feedback",
      priority: 5,
    })
  }

  if (
    term.counts.closed > 0 &&
    term.counts.published === 0 &&
    term.timelinePosition === "last"
  ) {
    rows.push({
      id: `${term.id}-publish`,
      termLabel,
      programName: term.programName,
      subject: `${term.counts.closed} evaluations`,
      state: "review",
      stateLabel: "Ready to publish",
      responseRate: term.closedResponseRate,
      actionLabel: "Publish results",
      priority: 5,
    })
  }

  if (term.needsAttention && term.attention) {
    rows.push({
      id: `${term.id}-attention`,
      termLabel,
      programName: term.programName,
      subject: `${term.attention.coursesBelowThreshold} courses · ${term.attention.facultyRolesBelowThreshold} faculty roles`,
      state: "attention",
      stateLabel: STATE_LABELS.attention,
      responseRate: term.closedResponseRate,
      actionLabel: "View analytics",
      priority: 0,
    })
  }

  return rows
}

const WORK_QUEUE_ACTION_LABEL: Record<EvaluationWorkQueueRow["state"], string> = {
  notSetup: "Schedule evaluations",
  draft: "Finish setup",
  scheduled: "Update schedule",
  live: "Remind or extend",
  closed: "Review feedback",
  review: "Publish results",
  attention: "View analytics",
}

function workQueueTermLabel(term: EvaluationTerm): string {
  return `${term.academicYear} · ${term.termName}`
}

function mapCourseToWorkQueueRow(
  term: EvaluationTerm,
  course: { id: string; courseCode: string; title: string; facultyName: string },
  state: EvaluationWorkQueueRow["state"],
  extras?: Partial<EvaluationWorkQueueRow>,
): EvaluationWorkQueueRow {
  return {
    id: course.id,
    termLabel: workQueueTermLabel(term),
    programName: term.programName,
    subject: `${course.courseCode} · ${course.title}`,
    state,
    stateLabel: extras?.stateLabel ?? STATE_LABELS[state],
    actionLabel: WORK_QUEUE_ACTION_LABEL[state],
    priority: 1,
    ...extras,
  }
}

/** Item-level rows for one work queue stage (sheet drill-in from compact summary). */
export function buildWorkQueueDetailRows(
  term: EvaluationTerm,
  focusState: EvaluationWorkQueueRow["state"],
  refDate: Date = EVALUATION_REFERENCE_DATE,
): EvaluationWorkQueueRow[] {
  switch (focusState) {
    case "notSetup":
      return scheduleCoursesForTerm(term, ["notSetup"]).map(course =>
        mapCourseToWorkQueueRow(term, course, "notSetup"),
      )
    case "draft":
      return scheduleCoursesForTerm(term, ["draft"]).map(course =>
        mapCourseToWorkQueueRow(term, course, "draft"),
      )
    case "scheduled": {
      const dueLabel = term.nextScheduledDate
        ? `Sends in ${daysUntil(term.nextScheduledDate, refDate)} days`
        : undefined
      return previewCourses(`${term.id}-scheduled`, term.counts.scheduled).map(course =>
        mapCourseToWorkQueueRow(term, course, "scheduled", { dueLabel }),
      )
    }
    case "live": {
      const dueLabel = term.nextClosingDate
        ? `Closes in ${daysUntil(term.nextClosingDate, refDate)} days`
        : undefined
      return previewCourses(`${term.id}-live`, term.counts.live).map((course, index) =>
        mapCourseToWorkQueueRow(term, course, "live", {
          dueLabel,
          responseRate:
            term.liveResponseRate != null
              ? Math.max(0, term.liveResponseRate - (index % 5) * 3)
              : undefined,
        }),
      )
    }
    case "closed":
      return previewCourses(`${term.id}-closed`, term.counts.closed).map((course, index) =>
        mapCourseToWorkQueueRow(term, course, "closed", {
          responseRate:
            term.closedResponseRate != null
              ? Math.max(0, term.closedResponseRate - (index % 3) * 2)
              : undefined,
        }),
      )
    case "review":
      return previewCourses(`${term.id}-review`, term.counts.closed).map(course =>
        mapCourseToWorkQueueRow(term, course, "review", {
          stateLabel: "Ready to publish",
          responseRate: term.closedResponseRate,
        }),
      )
    case "attention": {
      const courses = term.attention?.coursesBelowThreshold ?? 0
      const faculty = term.attention?.facultyRolesBelowThreshold ?? 0
      const courseRows = previewCourses(`${term.id}-attention-course`, courses).map(course =>
        mapCourseToWorkQueueRow(term, course, "attention", {
          subject: `${course.courseCode} · Below threshold`,
        }),
      )
      const facultyRows = previewCourses(`${term.id}-attention-faculty`, faculty).map(course =>
        mapCourseToWorkQueueRow(term, course, "attention", {
          subject: `${course.facultyName} · ${course.courseCode}`,
          stateLabel: "Faculty below threshold",
        }),
      )
      return [...courseRows, ...facultyRows]
    }
    default:
      return []
  }
}

export function buildEvaluationWorkQueue(): EvaluationWorkQueueRow[] {
  const refDate = EVALUATION_REFERENCE_DATE
  const terms = [
    ...getTermsByPosition("current"),
    ...getTermsByPosition("last"),
    ...getTermsByPosition("upcoming"),
  ]

  return terms
    .flatMap(term => buildTermWorkQueueRows(term, refDate))
    .sort((a, b) => a.priority - b.priority)
}
